import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePlatformConfig } from '../hooks/usePlatformConfig';

const Sidebar = ({ userType = 'customer' }) => {
  const location = useLocation();
  const { config } = usePlatformConfig();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    if (userType === 'operator' || userType === 'admin') {
      localStorage.removeItem('operator_token');
      navigate('/operator/login');
    } else {
      localStorage.removeItem('access_token');
      navigate('/login');
    }
  };

  // Icon components
  const Icon = ({ name }) => {
    const icons = {
      instances: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      ),
      snapshots: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      storage: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
      networking: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
      ),
      sshkeys: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
        </svg>
      ),
      marketplace: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      ),
      billing: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      alerts: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      dashboard: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      admin: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      logout: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      ),
      collapse: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
        </svg>
      ),
      expand: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
        </svg>
      )
    };
    return icons[name] || null;
  };

  // Navigation items for customer
  const customerNavItems = [
    {
      section: 'Compute',
      items: [
        { name: 'Instances', path: '/dashboard', icon: 'instances' },
        { name: 'Clusters', path: '/clusters', icon: 'marketplace' },
        { name: 'Snapshots', path: '/snapshots', icon: 'snapshots' },
      ]
    },
    {
      section: 'Storage & Network',
      items: [
        { name: 'Storage', path: '/storage', icon: 'storage' },
        { name: 'Networking', path: '/networking', icon: 'networking' },
        { name: 'SSH Keys', path: '/ssh-keys', icon: 'sshkeys' },
      ]
    },
    {
      section: 'Apps & Billing',
      items: [
        { name: 'Marketplace', path: '/marketplace', icon: 'marketplace' },
        { name: 'Billing', path: '/billing', icon: 'billing' },
        { name: 'Alerts', path: '/alerts', icon: 'alerts' },
      ]
    }
  ];

  // Navigation items for operator/admin
  const operatorNavItems = [
    {
      section: 'Management',
      items: [
        { name: 'Dashboard', path: '/operator/dashboard', icon: 'dashboard' },
        { name: 'Admin Panel', path: '/admin/dashboard', icon: 'admin' },
      ]
    }
  ];

  const navItems = userType === 'customer' ? customerNavItems : operatorNavItems;

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className={`${collapsed ? 'w-16' : 'w-64'} bg-slate-800 border-r border-slate-700 min-h-screen flex flex-col transition-all duration-300`}>
      {/* Logo/Brand */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
        {!collapsed && (
          <h1 className="text-xl font-bold text-white">⭐ {config.platform_name}</h1>
        )}
        {collapsed && (
          <h1 className="text-xl font-bold text-white mx-auto">⭐</h1>
        )}
      </div>

      {/* Navigation Items */}
      <div className="flex-1 overflow-y-auto py-4">
        {navItems.map((section, idx) => (
          <div key={idx} className="mb-6">
            {!collapsed && (
              <h3 className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                {section.section}
              </h3>
            )}
            <nav className="space-y-1">
              {section.items.map((item) => (
                <a
                  key={item.path}
                  href={item.path}
                  className={`
                    flex items-center px-4 py-2.5 text-sm font-medium transition-colors
                    ${isActive(item.path)
                      ? 'bg-slate-700 text-blue-400 border-r-2 border-blue-400'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    }
                  `}
                  title={collapsed ? item.name : ''}
                >
                  <span className="mr-3">
                    <Icon name={item.icon} />
                  </span>
                  {!collapsed && <span>{item.name}</span>}
                </a>
              ))}
            </nav>
          </div>
        ))}
      </div>

      {/* Collapse Toggle & User Menu */}
      <div className="border-t border-slate-700 p-4">
        {/* Collapse Button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full mb-3 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 rounded transition-colors flex items-center justify-center"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name={collapsed ? 'expand' : 'collapse'} />
          {!collapsed && <span className="ml-2">Collapse</span>}
        </button>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={`
            w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-slate-300 
            hover:bg-slate-700 hover:text-white rounded transition-colors
          `}
          title={collapsed ? 'Logout' : ''}
        >
          <Icon name="logout" />
          {!collapsed && <span className="ml-2">Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
